import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Upload, Brain, Target, TrendUp, Sparkle, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import AICopilot from '../components/AICopilot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CandidateDashboard = () => {
  const { user, logout } = useAuth();
  const [resumeText, setResumeText] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);
  const [uploadMode, setUploadMode] = useState('text'); // 'text' or 'file'

  useEffect(() => {
    fetchLatestAnalysis();
  }, []);

  const fetchLatestAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/candidate/latest-analysis`);
      if (response.data && !response.data.message) {
        setAnalysis(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch analysis:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validExtensions = ['.txt', '.pdf', '.docx'];
    const fileName = file.name.toLowerCase();
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType) {
      toast.error('Please upload a TXT, PDF, or DOCX file');
      return;
    }

    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setLoading(true);
    toast.info('Extracting text from file...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/candidate/upload-resume-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setResumeText(response.data.text);
        toast.success(`File uploaded! Extracted ${response.data.character_count} characters`);
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process file');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!resumeText.trim()) {
      toast.error('Please enter your resume text or upload a file');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/candidate/analyze-resume`, {
        resume_text: resumeText
      });
      setAnalysis(response.data);
      toast.success('Resume analyzed successfully!');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze resume');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-10">
        <div className="px-6 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tighter" data-testid="dashboard-title">TalentoX</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium" data-testid="user-name">{user?.full_name}</p>
              <p className="text-xs text-[#A1A1AA]" data-testid="user-role">Candidate</p>
            </div>
            <button
              onClick={logout}
              className="p-2 hover:bg-black/5 transition-colors rounded-none"
              data-testid="logout-button"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-8 lg:px-12 py-8">
        {/* Welcome Section */}
        <div className="mb-12">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-4">
            Career Dashboard
          </h2>
          <p className="text-base font-light text-[#52525B] max-w-2xl">
            Analyze your resume, get AI-powered insights, and prepare for interviews with our intelligent Career Copilot.
          </p>
        </div>

        {/* Control Room Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Resume Upload Card */}
          <div className="md:col-span-2 lg:col-span-2 bg-white border border-black/5 p-6 hover-lift" data-testid="resume-upload-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0033FF] flex items-center justify-center">
                <Upload size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold">Upload Resume</h3>
            </div>
            
            {/* Upload mode toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setUploadMode('text')}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors rounded-none ${
                  uploadMode === 'text'
                    ? 'bg-[#0033FF] text-white'
                    : 'bg-[#F9F9FB] text-[#52525B] border border-black/10 hover:bg-black/5'
                }`}
                data-testid="text-mode-button"
              >
                Paste Text
              </button>
              <button
                onClick={() => setUploadMode('file')}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors rounded-none ${
                  uploadMode === 'file'
                    ? 'bg-[#0033FF] text-white'
                    : 'bg-[#F9F9FB] text-[#52525B] border border-black/10 hover:bg-black/5'
                }`}
                data-testid="file-mode-button"
              >
                Upload File
              </button>
            </div>

            {uploadMode === 'text' ? (
              <textarea
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste your resume text here...&#10;&#10;Include your name, skills, experience, education, etc."
                className="w-full h-40 px-4 py-3 bg-[#F9F9FB] border border-black/10 rounded-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent outline-none resize-none font-light"
                data-testid="resume-input"
              />
            ) : (
              <div className="space-y-3">
                <label
                  htmlFor="resume-file-input"
                  className="flex flex-col items-center justify-center h-40 px-4 py-3 bg-[#F9F9FB] border-2 border-dashed border-black/10 rounded-none cursor-pointer hover:bg-black/5 transition-colors"
                  data-testid="file-upload-zone"
                >
                  <Upload size={32} className="text-[#0033FF] mb-2" />
                  <span className="font-medium text-[#0A0A0A]">Click to upload resume</span>
                  <span className="text-xs text-[#A1A1AA] mt-1">PDF, DOCX, or TXT (Max 10MB)</span>
                  {resumeText && (
                    <span className="text-xs text-[#0033FF] mt-2 font-medium">✓ File loaded ({resumeText.length} characters)</span>
                  )}
                </label>
                <input
                  id="resume-file-input"
                  type="file"
                  accept=".txt,.pdf,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="resume-file-input"
                />
              </div>
            )}
            
            <button
              onClick={handleAnalyze}
              disabled={loading || !resumeText.trim()}
              className="mt-4 bg-[#0033FF] text-white rounded-none font-medium px-6 py-3 hover:bg-[#002AE6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-center gap-2"
              data-testid="analyze-button"
            >
              {loading ? 'Analyzing with AI...' : 'Analyze with AI'}
              <Brain size={20} />
            </button>
          </div>

          {/* Quick Stats */}
          {analysis && (
            <>
              <div className="bg-white border border-black/5 p-6 hover-lift" data-testid="readiness-score-card">
                <div className="flex items-center gap-3 mb-4">
                  <Target size={24} className="text-[#0033FF]" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider">Readiness</h3>
                </div>
                <div className="text-5xl font-black tracking-tighter" data-testid="readiness-score">
                  {analysis.job_readiness_score}
                </div>
                <p className="text-xs text-[#A1A1AA] mt-1">Out of 100</p>
              </div>

              <div className="bg-white border border-black/5 p-6 hover-lift" data-testid="skill-gaps-card">
                <div className="flex items-center gap-3 mb-4">
                  <TrendUp size={24} className="text-[#0033FF]" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider">Skill Gaps</h3>
                </div>
                <div className="text-5xl font-black tracking-tighter" data-testid="skill-gaps-count">
                  {analysis.skill_gaps?.length || 0}
                </div>
                <p className="text-xs text-[#A1A1AA] mt-1">To address</p>
              </div>
            </>
          )}

          {/* AI Copilot Trigger */}
          <div
            onClick={() => setShowCopilot(true)}
            className="bg-[#0033FF] text-white p-6 cursor-pointer hover:bg-[#002AE6] transition-colors"
            data-testid="ai-copilot-trigger"
          >
            <div className="flex items-center gap-3 mb-4">
              <Sparkle size={24} weight="duotone" />
              <h3 className="text-sm font-semibold uppercase tracking-wider">AI Copilot</h3>
            </div>
            <p className="font-light text-sm">Practice interviews and get personalized career guidance</p>
          </div>

          {/* Analysis Results */}
          {analysis && (
            <>
              <div className="md:col-span-3 lg:col-span-2 bg-white border border-black/5 p-6" data-testid="strengths-card">
                <h3 className="text-lg font-semibold mb-4">Key Strengths</h3>
                <ul className="space-y-2">
                  {analysis.strengths?.map((strength, idx) => (
                    <li key={idx} className="flex items-start gap-2 font-light" data-testid={`strength-${idx}`}>
                      <span className="text-[#0033FF] mt-1">•</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="md:col-span-3 lg:col-span-2 bg-white border border-black/5 p-6" data-testid="recommendations-card">
                <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
                <ul className="space-y-2">
                  {analysis.recommendations?.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 font-light" data-testid={`recommendation-${idx}`}>
                      <span className="text-[#0033FF] mt-1">→</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="md:col-span-3 lg:col-span-4 bg-white border border-black/5 p-6" data-testid="skill-gaps-detail-card">
                <h3 className="text-lg font-semibold mb-4">Skill Gaps to Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.skill_gaps?.map((gap, idx) => (
                    <div
                      key={idx}
                      className="px-4 py-2 bg-[#F9F9FB] border border-black/5 font-light"
                      data-testid={`skill-gap-${idx}`}
                    >
                      {gap}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Copilot Modal */}
      {showCopilot && <AICopilot onClose={() => setShowCopilot(false)} />}
    </div>
  );
};

export default CandidateDashboard;
